import moment, { Moment } from 'moment';

import { applyGmtSuffixToDate, getCleanTimezoneAbbrev } from './timezone';

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December'
];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export const padZero = (num: number): string => {
  return num < 10 ? `0${num}` : `${num}`;
};

function getInitialDateOfMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

function getFinalDateOfMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0);
}

function getNumberOfDaysInMonth(date: Date = new Date()): number {
  return getFinalDateOfMonth(date).getDate();
}

function getNextMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1);
}

function getPreviousMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth() - 1, 1);
}

function getDatesOfMonth(date: Date = new Date()): Date[] {
  const numberOfDays = getNumberOfDaysInMonth(date);
  const initialDate = getInitialDateOfMonth(date);
  const dates: Date[] = [];
  for (let i = 0; i < numberOfDays; i++) {
    dates.push(new Date(initialDate.getFullYear(), initialDate.getMonth(), i + 1));
  }
  return dates;
}

function isDateToday(date: Date, timezoneGmtSuffix?: string): boolean {
  const today = new Date();

  if (timezoneGmtSuffix) {
    applyGmtSuffixToDate(today, timezoneGmtSuffix);
  }

  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
}

function isDateBeforeToday(date: Date, timezoneGmtSuffix?: string): boolean {
  const today = new Date();

  if (timezoneGmtSuffix) {
    applyGmtSuffixToDate(today, timezoneGmtSuffix);
  }

  return (
    date.getFullYear() < today.getFullYear() ||
    (date.getFullYear() === today.getFullYear() && date.getMonth() < today.getMonth()) ||
    (date.getFullYear() === today.getFullYear() &&
      date.getMonth() === today.getMonth() &&
      date.getDate() < today.getDate())
  );
}

function areDatesEqual(date1: Date, date2: Date): boolean {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
}

function isDateBetween(date: Date, startDate: Date, endDate: Date): boolean {
  return date >= startDate && date <= endDate;
}

function addMinutesToDate(date: Date, minutes: number): Date {
  return new Date(date.getTime() + minutes * 60000);
}

function addDaysToDate(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

function addMonthsToDate(date: Date, months: number): Date {
  const nextMonth = date.getMonth() + months;
  const nextMonthDate = Math.min(date.getDate(), getNumberOfDaysInMonth(new Date(date.getFullYear(), nextMonth)));
  return new Date(date.getFullYear(), nextMonth, nextMonthDate);
}

function getTimeRangeString({
  date1,
  date2,
  timezone,
  isSingleSession = true
}: {
  date1: string; // ISO8601 string
  date2: string; // ISO8601 string
  timezone: string;
  isSingleSession?: boolean;
}): string {
  const timezoneName = timezone?.split(' ')[0];
  if (!timezoneName) {
    return '';
  }

  const d1 = moment(date1).tz(timezoneName);
  const d2 = moment(date2).tz(timezoneName);
  const timezoneAbbrev = getCleanTimezoneAbbrev(d1.format('z'), timezone);

  const year1 = d1.format('YYYY');
  const year2 = d2.format('YYYY');
  const currentYear = String(new Date().getFullYear());
  const month1 = d1.format('MMMM');
  const month2 = d2.format('MMMM');
  const day1 = d1.format('D');
  const day2 = d2.format('D');

  let timeRangeString = '';
  if (year1 === year2 && month1 === month2 && day1 === day2) {
    if (year1 === currentYear) {
      if (isSingleSession) {
        timeRangeString = `${d1.format('MMM D, h:mm A')} - ${d2.format('h:mm A')} ${timezoneAbbrev}`;
      } else {
        timeRangeString = `${d1.format('MMM D, h:mm A z')}`;
      }
    } else {
      if (isSingleSession) {
        timeRangeString = `${d1.format('MMM D, YYYY, h:mm A')} - ${d2.format('h:mm A')} ${timezoneAbbrev}`;
      } else {
        timeRangeString = `${d1.format('MMM D, YYYY, h:mm A z')}`;
      }
    }
  } else if (year1 === year2 && month1 === month2 && day1 !== day2) {
    if (year1 === currentYear) {
      timeRangeString = `${d1.format('MMM D')} - ${d2.format('D')}, ${d1.format('h:mm A')} ${timezoneAbbrev}`;
    } else {
      timeRangeString = `${d1.format('MMM D')} - ${d2.format('D')}, ${d1.format('YYYY, h:mm A')} ${timezoneAbbrev}`;
    }
  } else if (year1 === year2 && month1 !== month2) {
    if (year1 === currentYear) {
      timeRangeString = `${d1.format('MMM D')} - ${d2.format('MMM D')}, ${d1.format('h:mm A')} ${timezoneAbbrev}`;
    } else {
      timeRangeString = `${d1.format('MMM D')} - ${d2.format('MMM D')}, ${d1.format('YYYY, h:mm A')} ${timezoneAbbrev}`;
    }
  } else {
    timeRangeString = `${d1.format('MMM D, YYYY')} - ${d2.format('MMM D, YYYY')}, ${d1.format('h:mm A')} ${timezoneAbbrev}`;
  }

  return timeRangeString.trim();
}

function getTomorrow(): Date {
  return addDaysToDate(new Date(), 1);
}

function convertDateToISO8601(date: Date | undefined, timezone: string): string {
  if (!date || !timezone) {
    return '';
  }

  const timezoneName = timezone.split(' ')[0];

  const year = date.getFullYear();
  const month = padZero(date.getMonth() + 1);
  const day = padZero(date.getDate());
  const dateString = `${year}-${month}-${day}`;

  const hours = padZero(date.getHours());
  const minutes = padZero(date.getMinutes());
  const seconds = padZero(date.getSeconds());

  const tz = moment.tz(`${dateString} ${hours}:${minutes}:${seconds}`, timezoneName).format('Z');

  return `${dateString}T${hours}:${minutes}:${seconds}${tz}`;
}

function getLocalDate(date: Date, timeZone: string): Date {
  const localDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return new Date(localDate.getTime() + localDate.getTimezoneOffset() * 60 * 1000);
}

function getFormattedDateInTimezone(
  date: Date,
  format: string,
  timezone: string,
  showTimezoneAbbreviation: boolean = false
): string {
  const timezoneIdentifier = timezone.split(' ')[0];

  if (!timezoneIdentifier) {
    return '';
  }

  if (!showTimezoneAbbreviation) {
    return moment(date).tz(timezoneIdentifier).format(format);
  }

  const timezoneAbbrev = getCleanTimezoneAbbrev(moment(date).tz(timezoneIdentifier).format('z'), timezone);

  return `${moment(date).tz(timezoneIdentifier).format(format)} ${timezoneAbbrev}`;
}

function getMidDateOfCurrentMonth(): Date {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 15);
}

function getDateDifference({
  startDate,
  endDate,
  unitOfTime
}: {
  startDate: Moment;
  endDate: Moment;
  unitOfTime: moment.unitOfTime.Diff | undefined;
}): number {
  return Math.abs(startDate.diff(endDate, unitOfTime));
}

export {
  DAYS,
  MONTHS,
  getInitialDateOfMonth,
  getFinalDateOfMonth,
  getNumberOfDaysInMonth,
  getNextMonth,
  getPreviousMonth,
  getDatesOfMonth,
  isDateToday,
  isDateBeforeToday,
  areDatesEqual,
  isDateBetween,
  addMinutesToDate,
  addDaysToDate,
  addMonthsToDate,
  getTimeRangeString,
  getTomorrow,
  convertDateToISO8601,
  getLocalDate,
  getFormattedDateInTimezone,
  getMidDateOfCurrentMonth,
  getDateDifference
};
